import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, ShareNetwork, Warning } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PostComposer } from "../components/social/PostComposer";
import { SocialCalendar } from "../components/social/SocialCalendar";
import { PostList } from "../components/social/PostList";
import { SocialStats } from "../components/social/SocialStats";
import { SocialSettings } from "../components/social/SocialSettings";
import { errMsg } from "../components/social/socialShared";

export default function SocialMedia() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "calendar");
  const [accounts, setAccounts] = useState(null);
  const [composer, setComposer] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const loadAccounts = () => api.get("/social/accounts").then((r) => setAccounts(r.data.accounts))
    .catch((e) => { toast.error(errMsg(e)); if (e?.response?.status === 403) nav("/modules"); else setAccounts([]); });
  useEffect(() => { loadAccounts(); }, []); // eslint-disable-line
  useEffect(() => {
    if (params.get("connected")) { toast.success("Compte connecté"); }
    if (params.get("oauth_error")) { toast.error(params.get("oauth_error")); }
    if (params.get("connected") || params.get("oauth_error")) { setParams({ tab: params.get("tab") || "settings" }, { replace: true }); }
  }, []); // eslint-disable-line

  const changeTab = (t) => { setTab(t); setParams({ tab: t }, { replace: true }); };
  if (!accounts) return null;
  const noneConnected = accounts.length > 0 && !accounts.some((a) => a.connected);

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      <button type="button" onClick={() => nav("/modules")} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="social-back">
        <ArrowLeft size={14} /> Modules
      </button>
      <header className="mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-display font-bold flex items-center gap-2" data-testid="social-title"><ShareNetwork size={28} /> Réseaux sociaux</h1>
          <p className="mt-1 text-sm text-muted-foreground">Un post, plusieurs réseaux. Programmez à l'avance et suivez les résultats.</p>
        </div>
        <Button onClick={() => setComposer({})} data-testid="social-new-post"><Plus size={16} className="mr-1" />Nouveau post</Button>
      </header>
      {noneConnected && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="social-no-account-banner">
          <Warning size={18} className="mt-0.5 shrink-0" />
          <span>Aucun réseau n'est encore connecté. Vous pouvez préparer et programmer des posts ; ils partiront une fois les comptes reliés{isAdmin ? " dans l'onglet Réglages" : " par un admin"}.</span>
        </div>
      )}
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="mb-4 max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="calendar" data-testid="social-tab-calendar">Calendrier</TabsTrigger>
          <TabsTrigger value="posts" data-testid="social-tab-posts">Posts</TabsTrigger>
          <TabsTrigger value="stats" data-testid="social-tab-stats">Statistiques</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="social-tab-settings">Réglages</TabsTrigger>}
        </TabsList>
        <TabsContent value="calendar"><SocialCalendar refreshKey={refreshKey} onOpen={(p) => setComposer({ post: p })} onNew={(d) => setComposer({ date: d })} /></TabsContent>
        <TabsContent value="posts"><PostList refreshKey={refreshKey} isAdmin={isAdmin} userId={user?.id} onEdit={(p) => setComposer({ post: p })} onChanged={bump} /></TabsContent>
        <TabsContent value="stats"><SocialStats refreshKey={refreshKey} /></TabsContent>
        {isAdmin && <TabsContent value="settings"><SocialSettings accounts={accounts} onChanged={loadAccounts} /></TabsContent>}
      </Tabs>
      <PostComposer open={!!composer} onClose={() => setComposer(null)} post={composer?.post} defaultDate={composer?.date}
        accounts={accounts} isAdmin={isAdmin} onSaved={bump} />
    </div>
  );
}
